import React, { useState } from 'react';
import { useClassName, useWindowSize } from 'common/hooks';
import { optimizeContentfulImageUrl } from 'common/components';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer';
import { CTA_TYPES, STANDARD_EVENTS } from 'utils/analytics/trackV2';
import useCustomStyle from '../../hooks';
import Link from 'public/screens/CustomLandingPage/Components/Link';
import { PriceOptionsSelector } from 'public/components/AttractionScreen/PriceOptionsSelector';
import { BottomSheet } from 'public/components/BottomSheet';
import { getCheapestOption } from './CardTypeU';

import './card-w.less';

export default function CardTypeW({ item, refs, handleTrack }) {
  const classNames = useClassName('CLPCardTypeW');
  const [setRef] = useCustomStyle(item.fields.settings);
  const { isMobile, isTablet } = useWindowSize();
  const [isPassSheetOpen, setIsPassSheetOpen] = useState(false);
  const banner = item.fields.subtitle;
  const bannerIcon = item.fields.icon;
  const options = item.fields.itemDetails?.fields?.items || [];

  const primaryIndex = options.findIndex((option) =>
    (option.fields.tags || []).includes('best-value')
  );
  // When no option is flagged "best-value" (e.g. equal-price options), default
  // to the last option rather than the first.
  const defaultIndex = primaryIndex >= 0 ? primaryIndex : options.length - 1;
  const [selectedIndex, setSelectedIndex] = useState(defaultIndex);

  if (!options.length) return null;

  const selected = options[selectedIndex];
  const selectedLink = selected.fields.links?.[0];

  // On mobile/tablet the card is a sticky bar; the options open in a bottom
  // sheet instead of rendering inline (same behavior as CardTypeU).
  if (options.length > 1 && (isMobile || isTablet)) {
    const priceOptions = options.map((option) => ({
      name: option.fields.title,
      price: option.fields.subtitle,
      priceLabel: option.fields.labels?.[0],
      description:
        option.fields.description &&
        documentToPlainTextString(option.fields.description),
      type: (option.fields.tags || []).includes('best-value')
        ? 'primary'
        : undefined,
      link: option.fields.links?.[0]?.fields?.url,
    }));

    const handleGetPasses = (url) => {
      if (!handleTrack) return;
      handleTrack({
        event: STANDARD_EVENTS.click,
        params: {
          label: 'Get Passes',
          cta_type: CTA_TYPES.link,
          destination_url: url,
        },
      });
    };

    return (
      <div ref={setRef} className={classNames(['container', 'container--bar'])}>
        <div className={classNames('bar-pricing')}>
          <span className={classNames('bar-pricing-label')}>From</span>
          <strong className={classNames('bar-pricing-value')}>
            {getCheapestOption(priceOptions).price}
          </strong>
        </div>
        <button
          type="button"
          className={classNames('bar-cta')}
          onClick={() => setIsPassSheetOpen(true)}>
          Pick your main park
        </button>
        <BottomSheet
          className="bundles-pass-sheet"
          isOpen={isPassSheetOpen}
          onClose={() => setIsPassSheetOpen(false)}
          title="Pick your main park"
          content={
            <PriceOptionsSelector
              layout="list"
              options={priceOptions}
              banner={banner}
              bannerIcon={bannerIcon}
              onClick={handleGetPasses}
            />
          }
        />
      </div>
    );
  }

  return (
    <div ref={setRef} className={classNames('container')}>
      {banner && (
        <div className={classNames('banner')}>
          {bannerIcon?.fields?.file && (
            <img
              src={optimizeContentfulImageUrl(
                bannerIcon.fields.file.url,
                bannerIcon.fields.file.contentType
              )}
              alt=""
              aria-hidden="true"
              className={classNames('banner-icon')}
            />
          )}
          {banner}
        </div>
      )}
      <div className={classNames('cards')}>
        {options.map((option, index) => {
          const isSelected = index === selectedIndex;
          const isBestValue = (option.fields.tags || []).includes('best-value');
          return (
            <div
              key={option.sys.id}
              role="radio"
              tabIndex={0}
              aria-checked={isSelected}
              onClick={() => setSelectedIndex(index)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  setSelectedIndex(index);
                }
              }}
              className={classNames(['card', isSelected && 'card--selected'])}>
              {isBestValue && (
                <span className={classNames('badge')}>Best Value</span>
              )}
              <span className={classNames('radio')} />
              <span className={classNames('name')}>{option.fields.title}</span>
              {option.fields.subtitle && (
                <strong className={classNames('price')}>
                  {option.fields.subtitle}
                </strong>
              )}
              {option.fields.labels?.[0] && (
                <span className={classNames('price-label')}>
                  {option.fields.labels[0]}
                </span>
              )}
              {option.fields.labels?.[0] && option.fields.description && (
                <span className={classNames('divider')} />
              )}
              {option.fields.description && (
                <div className={classNames('description')}>
                  {documentToReactComponents(option.fields.description)}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {selectedLink && (
        <div className={classNames('cta')}>
          <Link
            key={selectedLink.sys.id}
            component={selectedLink}
            refs={refs}
            handleTrack={handleTrack}
          />
        </div>
      )}
    </div>
  );
}
